import { zodResolver } from '@hookform/resolvers/zod';
import { forgotPasswordRequestSchema, type ForgotPasswordRequest } from '@recipes/contracts';
import { Alert, Button, Input } from '@recipes/ui';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { FormField } from '@/components/forms/form-field';
import { AuthModeLoading, IdentityAccountLink, useAuthMode } from '@/features/auth/identity';
import { api } from '@/lib/api';
import { describeError } from '@/lib/errors';
import { AuthLayout } from './auth-layout';

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const form = useForm<ForgotPasswordRequest>({
    resolver: zodResolver(forgotPasswordRequestSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      await api.auth.forgotPassword(values);
      setSent(true);
    } catch (err) {
      setError(describeError(err));
    }
  });

  const authMode = useAuthMode();
  if (authMode.mode === undefined) return <AuthModeLoading />;
  if (authMode.mode === 'oidc' && authMode.identity) {
    return (
      <AuthLayout
        title={t('auth.resetPassword')}
        description={t('auth.identity.forgotDescription')}
        testId="forgot-password-page"
      >
        <IdentityAccountLink
          identity={{ ...authMode.identity, accountUrl: `${authMode.identity.issuer}/forgot` }}
          label={t('auth.resetPassword')}
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('auth.resetPassword')}
      description={t('auth.forgotDescription')}
      testId="forgot-password-page"
      footer={
        <Link to="/login" className="font-medium text-primary underline-offset-4 hover:underline">
          {t('auth.backToLogin')}
        </Link>
      }
    >
      {sent ? (
        <Alert variant="success" data-testid="forgot-sent">
          {t('auth.resetSent')}
        </Alert>
      ) : (
        <form onSubmit={onSubmit} noValidate className="space-y-4">
          {error && <Alert variant="destructive">{error}</Alert>}
          <FormField
            label={t('auth.email')}
            error={form.formState.errors.email && t('auth.errors.email')}
            required
          >
            <Input
              type="email"
              autoComplete="email"
              inputMode="email"
              data-testid="forgot-email"
              {...form.register('email')}
            />
          </FormField>
          <Button
            type="submit"
            className="w-full"
            loading={form.formState.isSubmitting}
            data-testid="forgot-submit"
          >
            {t('auth.sendResetLink')}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
